/**
 * utils/tokenCounter.js
 *
 * Client-side token estimation for the public chat token counter.
 *
 * Uses a word-based heuristic (~1.3 tokens per word for English text,
 * ~2.5 tokens per word for code). This is a ±10% approximation which
 * is fine for a UI counter. For exact counts you'd need model-specific
 * tokenizers (tiktoken for OpenAI, etc.) which are heavy client-side.
 *
 * Usage:
 *   import { estimateTokens, countContextTokens } from "../utils/tokenCounter";
 *   const total = countContextTokens({ systemPrompt, currentMessage, history, historyDepth });
 */

/**
 * Estimate token count for a string.
 * Heuristic: split on whitespace + punctuation boundaries.
 * - Average English text: ~1.3 tokens per word
 * - Code: ~2.5 tokens per word (more punctuation, short identifiers)
 * - We use 1.4 as a middle ground
 *
 * @param {string} text
 * @returns {number}
 */
export function estimateTokens(text) {
  if (!text) return 0;
  // Split on whitespace and count. Each "word" is roughly 1.3-1.5 tokens.
  // Also count punctuation clusters as partial tokens.
  const words = text.split(/\s+/).filter(Boolean);
  const charCount = text.length;

  // Use character-based estimation as a cross-check:
  // ~4 characters per token for English, ~3.5 for code
  const charEstimate = Math.ceil(charCount / 3.8);

  // Word-based estimate
  const wordEstimate = Math.ceil(words.length * 1.4);

  // Take the higher of the two (more conservative)
  return Math.max(charEstimate, wordEstimate);
}

/**
 * Count total context tokens that would be sent to the model.
 *
 * @param {object} opts
 * @param {string}  opts.systemPrompt   — System prompt text (or "" for default)
 * @param {string}  opts.currentMessage — Current text in the input box
 * @param {Array}   opts.history        — Array of message entries [{type, question, answer}]
 * @param {number}  opts.historyDepth   — Number of Q+A pairs to include (default 5)
 * @returns {{ total: number, breakdown: { system: number, history: number, current: number } }}
 */
export function countContextTokens({
  systemPrompt = "",
  currentMessage = "",
  history = [],
  historyDepth = 5,
}) {
  // System prompt tokens
  const defaultPrompt = "You are a helpful assistant.";
  const systemText = systemPrompt || defaultPrompt;
  const systemTokens = estimateTokens(systemText);

  // History tokens — last N Q+A pairs
  // Walk backwards to find the last historyDepth pairs
  const pairs = [];
  let count = 0;
  for (let i = history.length - 1; i >= 0 && count < historyDepth; i--) {
    const entry = history[i];
    if (
      entry.type === "answer" &&
      i > 0 &&
      history[i - 1]?.type === "question"
    ) {
      pairs.unshift({ question: history[i - 1], answer: entry });
      count++;
      i--; // skip the question since we consumed it
    }
  }

  let historyTokens = 0;
  for (const pair of pairs) {
    historyTokens += estimateTokens(pair.question.question || "");
    historyTokens += estimateTokens(pair.answer.answer || "");
  }

  // Add ~4 tokens per message for role/formatting overhead
  historyTokens += pairs.length * 8; // 4 tokens per Q + 4 per A

  // Current message tokens
  const currentTokens = estimateTokens(currentMessage);

  // System message overhead (~4 tokens for role)
  const total = systemTokens + 4 + historyTokens + currentTokens + 4;

  return {
    total,
    breakdown: {
      system: systemTokens + 4,
      history: historyTokens,
      current: currentTokens + 4,
    },
  };
}

/**
 * Format a token count for display.
 * @param {number} count
 * @returns {string} e.g. "1,234" or "128K"
 */
export function formatTokenCount(count) {
  if (count >= 100000) {
    return `${Math.round(count / 1000)}K`;
  }
  return count.toLocaleString();
}
